import { useUser } from 'hooks'
import React, { useState, useEffect, createContext, useCallback, useMemo, useRef } from 'react'
import { IChatMember, IChatMessage, IChatRoom } from 'types/chatInterfaces'
import { IError } from 'types/error'
import { IAccount } from 'types/userInterfaces'
import { chatAPI } from 'api/ChatApi'
import { cloneDeep, orderBy } from 'lodash'
import { Encrypt } from 'utils/aes'
import { useTranslation } from 'react-i18next'
import CaleoYellowButton from 'Components/reusable/Buttons/CaleoYellowButton'
import { setSocketAuthorization, socket } from 'api/WebSocket'
import { useLocation, useNavigate } from 'react-router'

export interface ChatState {
  isConnected: boolean
  chatSecret: string
  allowedUsers: IAccount[]
  userRooms: IChatRoom[]
  roomData: IChatRoom | undefined
  userInvites: IChatMember[]
  newMessage: Date | undefined
  loading: boolean
  error: IError | undefined
  fullyRendered: boolean
  connecting: boolean | undefined
  showNotification: boolean
  notificationData?: {
    message: string
    type: 'error' | 'warning' | 'info' | 'success'
    duration?: 'short' | 'long' | 'indefinite'
    action?: React.ReactNode
  }
  closeNotification: () => void
  setFullyRendered: (value: boolean) => void
  setIsConnected: (value: boolean) => void
  setUserRooms: (value: IChatRoom[]) => void
  setRoomData: (value: IChatRoom | undefined) => void
  setUserInvites: (value: IChatMember[]) => void
  setLoading: (value: boolean) => void
  setError: (value: IError | undefined) => void
  connect: (user: IAccount) => Promise<void>
  joinRoom: (name: IChatRoom['name'], noRedirect?: boolean) => Promise<IChatRoom | undefined>
  leaveRoom: (name: IChatRoom['name']) => Promise<void>
  sendMessage: (room: IChatMessage['roomName'], message: IChatMessage['message']) => Promise<void>
  deleteMessage: (messageId: IChatMessage['id']) => Promise<void>
  createRoom: (
    data: IChatRoom,
    noRedirect?: boolean,
    noJoin?: boolean,
    controller?: AbortController
  ) => Promise<IChatRoom | undefined>
  saveRoom: (data: IChatRoom, controller?: AbortController) => Promise<IChatRoom | undefined>
  deleteRoom: (data: IChatRoom, controller?: AbortController) => Promise<void>
  getRoomsInvites: () => Promise<void>
  sendUser: (userId: number) => void
  invite: (room: IChatRoom['name'], accountIds: number[]) => void
  getMoreMessages: (room: IChatRoom['name'], lastMessage: IChatMessage) => void
  sendNotification: (type: string, accountId: number, message?: string) => void
  reconnect: (name?: string) => void
  disconnect: () => void
}

export const ChatContext = createContext<ChatState>({} as ChatState)

const { Provider } = ChatContext

/**
 * Provider for handling chat operations in the application.
 *
 * @param children
 * @returns Provider for handling chat operations.
 * @notExported
 */
const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, features } = useUser()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const [isConnected, setIsConnected] = useState<boolean>(false)
  const [chatSecret, setChatSecret] = useState<string>('')
  const [allowedUsers, setAllowedUsers] = useState<IAccount[]>([])
  const [userRooms, setUserRooms] = useState<IChatRoom[]>([])
  const [roomData, setRoomData] = useState<IChatRoom>()
  const [userInvites, setUserInvites] = useState<IChatMember[]>([])
  const [newMessage, setNewMessage] = useState<Date>()
  const [loading, setLoading] = useState<boolean>(true)
  const [fullyRendered, setFullyRendered] = useState<boolean>(false)
  const [error, setError] = useState<IError>()
  const [connecting, setConnecting] = useState<boolean>()
  const [showNotification, setShowNotification] = useState<boolean>(false)
  const [notificationData, setNotificationData] = useState<{
    message: string
    type: 'error' | 'warning' | 'info' | 'success'
    duration?: 'short' | 'long' | 'indefinite'
    action?: React.ReactNode
  }>()

  const creatingRoomsRef = useRef<Set<string>>(new Set())
  const recentlyLeftRef = useRef<Map<string, number>>(new Map())

  /**
   * Get users invites to rooms.
   *
   * @returns Returns if still connecting.
   * @notExported
   */
  const getRoomsInvites = useCallback(async () => {
    try {
      setLoading(true)
      setUserRooms(await chatAPI.getRooms())
      setUserInvites(await chatAPI.getInvites())
    } catch (error) {
      setError(error as IError)
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Emit user ID when connected.
   * This then sets secret for encrypting messages.
   *
   * @param userId - Account ID.
   * @notExported
   */
  const sendUser = useCallback(
    (userId: number) => {
      socket.emit('user', userId, (status: string, secret: string) => {
        if (status === 'OK') {
          setChatSecret(secret)
          setIsConnected(true)
          getRoomsInvites()
        }
        setLoading(false)
      })
    },
    [getRoomsInvites]
  )

  /**
   * Function to connect to Socket IO server.
   *
   * @param user - User data.
   * @returns Returns if already connecting.
   * @notExported
   */
  const connect = useCallback(
    async (user: IAccount) => {
      setLoading(true)
      if (connecting) return

      setConnecting(true)
      setIsConnected(false)

      if (!socket.connected) {
        setSocketAuthorization()

        socket.connect()
      }

      sendUser(user.id)
      setConnecting(false)
    },
    [connecting, sendUser]
  )

  const disconnect = useCallback(() => {
    if (socket.connected || isConnected) {
      socket.disconnect()
      setIsConnected(false)
      setConnecting(false)
      setChatSecret('')
      setAllowedUsers([])
      setUserRooms([])
      setRoomData(undefined)
      setUserInvites([])
      setNewMessage(undefined)
      setFullyRendered(false)
    }
  }, [isConnected])

  /**
   * Join room function. Sets room data after successfull join.
   *
   * @param name - Room name.
   * @param noRedirect - Should user be redirected.
   * @notExported
   */
  const joinRoom = useCallback(
    async (name: IChatRoom['name'], noRedirect?: boolean) => {
      // prevent immediate rejoin if this room was recently left
      const leftAt = recentlyLeftRef.current.get(name)
      if (leftAt && Date.now() - leftAt < 2000) {
        const err = { name: 'RecentlyLeft', message: 'Room was recently left' } as unknown as IError
        console.debug('joinRoom: prevented rejoining recently left room', name)
        throw err
      }

      if (socket.connected && user) {
        try {
          setLoading(true)

          const room = await new Promise<IChatRoom>((resolve, reject) => {
            let settled = false

            const onError = (err: IError) => {
              if (!settled) {
                settled = true
                console.debug('joinRoom: socket error for', name, err)
                reject(err)
              }
            }

            socket.once('error', onError)

            const timeout = setTimeout(() => {
              if (!settled) {
                settled = true
                socket.off('error', onError)
                const timeoutError = { name: 'TimeoutError', message: 'Timed out while joining room' } as IError
                console.debug('joinRoom: timeout while joining', name)
                reject(timeoutError)
              }
            }, 3000)

            socket.emit('joinRoom', name, (room: IChatRoom) => {
              if (!settled) {
                settled = true
                clearTimeout(timeout)
                socket.off('error', onError)
                resolve(room)
              }
            })
          })

          setRoomData(room)
          if (!noRedirect) {
            navigate(`/chat/${room.name}`)
          }

          return room
        } catch (error) {
          setError(error as IError)
          throw error
        } finally {
          setLoading(false)
        }
      }

      return undefined
    },
    [navigate, user]
  )

  /**
   * Reconnection function.
   *
   * @param name - Chat room name.
   * @notExported
   */
  const reconnect = useCallback(
    async (name?: string) => {
      if (user && name) {
        await connect(user)
        await joinRoom(name, true)
      }
      if (user && !name) {
        await connect(user)
      }
    },
    [connect, joinRoom, user]
  )

  /**
   * Leave room function. Empties room data and updates userRooms list.
   *
   * @param name - Room name.
   * @notExported
   */
  const leaveRoom = useCallback(
    async (name: IChatRoom['name']) => {
      if (socket.connected && user) {
        try {
          setLoading(true)
          socket.emit('leaveRoom', name, (success: boolean) => {
            if (success) {
              // record leave to prevent immediate re-joins caused by route/effect races
              recentlyLeftRef.current.set(name, Date.now())
              // remove entry after short delay
              setTimeout(() => recentlyLeftRef.current.delete(name), 2000)

              setRoomData(undefined)
              setUserRooms(userRooms.filter(room => room.name !== name))
              if (!location.pathname.includes('/assignments') && !location.pathname.includes('/workorders')) {
                navigate('/chat')
              }
            }
          })
        } catch (error) {
          setError(error as IError)
        } finally {
          setLoading(false)
        }
      }
    },
    [location, userRooms, navigate, user]
  )

  /**
   * Encrypts and emits chat message to room.
   *
   * @param room - Room name.
   * @param message - Message text.
   * @notExported
   */
  const sendMessage = useCallback(
    async (room: IChatMessage['roomName'], message: IChatMessage['message']) => {
      if (socket.connected && user && message && chatSecret) {
        try {
          setLoading(true)
          socket.emit('chat', room, Encrypt(message, chatSecret))
        } catch (error) {
          setError(error as IError)
        } finally {
          setLoading(false)
        }
      }
    },
    [chatSecret, user]
  )

  /**
   * Deletes a message with the given ID from the chat.
   *
   * @param messageId - The ID of the message to be deleted.
   * @return A promise that resolves when the message is successfully deleted.
   * @notExported
   */
  const deleteMessage = useCallback(
    async (messageId: IChatMessage['id']) => {
      if (socket.connected && user) {
        try {
          setLoading(true)
          socket.emit('deleteChat', messageId)
        } catch (error) {
          setError(error as IError)
        } finally {
          setLoading(false)
        }
      }
    },
    [user]
  )

  /**
   * Create new room function.
   * Saves new room data and joins the room and updates userRooms list.
   *
   * @param data - Room data.
   * @param noRedirect - Should user be redirected.
   * @notExported
   */
  const createRoom = useCallback(
    async (data: IChatRoom, noRedirect?: boolean, noJoin?: boolean, controller?: AbortController) => {
      if (!data?.name) return
      if (creatingRoomsRef.current.has(data.name)) return
      creatingRoomsRef.current.add(data.name)

      try {
        setLoading(true)
        const newRoom = await chatAPI.create(data, controller)
        setUserRooms(await chatAPI.getRooms(controller))

        if (!noJoin) {
          // Decide whether we should prevent navigation when joining.
          const shouldNoRedirect =
            noRedirect ||
            (location && (location.pathname.includes('/assignments') || location.pathname.includes('/workorders')))

          // Try to join room with retries for cases where the socket server hasn't picked up the new room yet.
          let attempts = 0
          const maxAttempts = 5
          while (attempts < maxAttempts) {
            try {
              await joinRoom(newRoom.name, shouldNoRedirect)
              break
            } catch (err) {
              const e = err as IError
              console.debug('createRoom: join attempt failed for', newRoom.name, 'attempt', attempts + 1, e)
              if (e && (e.status === 404 || e.name === 'TimeoutError')) {
                attempts += 1
                if (attempts >= maxAttempts) {
                  setError(e)
                  break
                }
                await new Promise(r => setTimeout(r, 500))
                continue
              }
              throw err
            }
          }
        }

        setLoading(false)
        return newRoom
      } catch (err) {
        const error = err as IError
        setLoading(false)
        setError(error)
        if (error.response && error.response.status !== 400) {
          throw error
        }
        return undefined
      } finally {
        creatingRoomsRef.current.delete(data.name)
      }
    },
    [joinRoom, location]
  )

  /**
   * Save updated room data.
   *
   * @param data - Updated room data.
   * @returns Resulted room data.
   * @notExported
   */
  const saveRoom = useCallback(
    async (data: IChatRoom, controller?: AbortController) => {
      try {
        setLoading(true)
        const updatedRoom = await chatAPI.save(data, controller)
        const newRooms = [...userRooms]
        const index = newRooms.findIndex(room => room.name === updatedRoom.name)
        newRooms[index] = updatedRoom
        setUserRooms(newRooms)
        setLoading(false)
        return updatedRoom
      } catch (error) {
        setLoading(false)
        setError(error as IError)
        throw error
      }
    },
    [userRooms]
  )

  /**
   * Deletes room data and updates userRooms list.
   *
   * @param data - Room data to be deleted.
   * @notExported
   */
  const deleteRoom = useCallback(
    async (data: IChatRoom, controller?: AbortController) => {
      try {
        setLoading(true)
        await chatAPI.delete(data.id, controller)
        setUserRooms(previous => previous.filter(room => room.id !== data.id))
        setUserInvites(previous => previous.filter(invite => invite.roomName !== data.name))
      } catch (error) {
        setError(error as IError)
      } finally {
        setLoading(false)
      }
    },
    [setUserRooms, setUserInvites]
  )

  /**
   * Invite new acounts to chat room.
   *
   * @param room - Room name.
   * @param accountIds - Account IDs to invite.
   * @notExported
   */
  const invite = useCallback(
    (room: IChatRoom['name'], accountIds: number[]) => {
      if (socket.connected && user) {
        try {
          setLoading(true)
          socket.emit('newInvite', room, accountIds)
        } catch (error) {
          setError(error as IError)
        } finally {
          setLoading(false)
        }
      }
    },
    [user]
  )

  /**
   * Get more messages for room.
   *
   * @param room - Room name.
   * @param lastMessage - Oldest visible message.
   * @notExported
   */
  const getMoreMessages = useCallback(
    (room: IChatRoom['name'], lastMessage: IChatMessage) => {
      if (socket.connected && user && roomData && lastMessage) {
        try {
          setLoading(true)
          if (room === roomData.name) {
            socket.emit('moreMessages', room, lastMessage, (messages: IChatMessage[]) => {
              if (messages.length) {
                setRoomData(previous => {
                  if (previous && previous.Messages) {
                    return {
                      ...previous,
                      Messages: [...previous.Messages, ...messages],
                    }
                  }
                })
              }
            })
          }
        } catch (error) {
          setError(error as IError)
        } finally {
          setLoading(false)
        }
      }
    },
    [roomData, user]
  )

  /**
   * Send notification to user.
   *
   * @param type - Notification type.
   * @param accountId - Notification target account ID.
   * @param message - Notification message.
   * @notExported
   */
  const sendNotification = useCallback(
    (type: string, accountId: number, message?: string) => {
      if (socket.connected && user) {
        try {
          socket.emit('notify', type, accountId, message)
        } catch (error) {
          setError(error as IError)
        } finally {
          setLoading(false)
        }
      }
    },
    [user]
  )

  const closeNotification = useCallback(() => {
    setShowNotification(false)
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (features && features.includes('chat')) {
        setAllowedUsers(orderBy(await chatAPI.getAllowed(controller), ['organizationId'], ['asc']))
      }
    })()

    return () => {
      controller.abort()
    }
  }, [isConnected, features])

  useEffect(() => {
    /**
     * Event handler for member join.
     *
     * @param incomingData - Room data.
     * @notExported
     */
    function onMemberJoin(incomingData: IChatRoom) {
      if (roomData && roomData.name === incomingData.name) {
        const newRoomData = cloneDeep(roomData)

        if (newRoomData && newRoomData.Members && incomingData.name === newRoomData.name) {
          setRoomData(incomingData)
        }
      }
    }

    /**
     * Event handler for member leave.
     *
     * @param leavingMember - Leaving chat member.
     * @notExported
     */
    function onMemberLeave(leavingMember: IChatMember) {
      const newRoomData = cloneDeep(roomData)
      if (newRoomData && newRoomData.Members && newRoomData.name === leavingMember.roomName) {
        newRoomData.Members = newRoomData.Members.filter(member => member.id !== leavingMember.id)

        setRoomData(newRoomData)
      }
    }

    /**
     * Event handler for new invite.
     *
     * @param member - Member invite.
     * @notExported
     */
    function onInvite(member: IChatMember) {
      setUserInvites(previous => [...previous, member])
    }

    /**
     * Event handler for room edit.
     *
     * @param updatedRoom - Updated room data.
     * @notExported
     */
    function onRoomEdit(updatedRoom: IChatRoom) {
      if (updatedRoom) {
        if (userRooms) {
          const newUserRooms = cloneDeep(userRooms)
          const userIndex = newUserRooms.findIndex(room => room.id === updatedRoom.id)
          newUserRooms[userIndex] = updatedRoom
          setUserRooms(newUserRooms)
        }

        if (roomData && roomData.name === updatedRoom.name) {
          setRoomData(updatedRoom)
        }
      }
    }

    /**
     * Event handler for room delete.
     *
     * @param deletedRoom - Deleted room name.
     * @notExported
     */
    function onRoomDelete(deletedRoom: string) {
      if (userRooms) {
        const newUserRooms = userRooms.filter(room => room.name !== deletedRoom)
        setUserRooms(newUserRooms)
      }

      if (userInvites) {
        const newUserInvites = userInvites.filter(room => room.roomName !== deletedRoom)
        setUserInvites(newUserInvites)
      }

      if (roomData && roomData.name === deletedRoom) {
        setRoomData(undefined)
      }
    }

    /**
     * Event handler for new message.
     *
     * @param message - New message.
     * @notExported
     */
    async function onNewMessage(message: IChatMessage) {
      if (roomData && roomData.name === message.roomName) {
        setRoomData(previous => {
          if (previous) {
            if (previous.Messages) {
              return { ...previous, Messages: [...previous.Messages, message] }
            } else {
              return { ...previous, Messages: [message] }
            }
          }
          return previous
        })

        const newUserRooms = cloneDeep(userRooms)
        if (newUserRooms) {
          const index = newUserRooms.findIndex(room => room.name === message.roomName)
          const newRoomData = newUserRooms[index]
          if (newRoomData) {
            if (newRoomData.Messages) {
              newRoomData.Messages.push(message)
            } else {
              newRoomData.Messages = [message]
            }
            newUserRooms[index] = newRoomData
            setUserRooms(newUserRooms)
          }
        }
        setNewMessage(new Date())
      } else if (user && !roomData) {
        setRoomData(await chatAPI.getRoom(message.roomName))
      }
    }

    /**
     * Deletes a message from the room's message list.
     *
     * @param {number} messageId - The ID of the message to be deleted.
     * @notExported
     */
    function onMessageDeleted(messageId: number) {
      if (roomData && roomData.Messages) {
        setRoomData({ ...roomData, Messages: roomData.Messages.filter(message => message.id !== messageId) })
      }
    }

    /**
     * Event handler for new notification.
     *
     * @param type - Notification type.
     * @param message - Notification message.
     * @param url - Notification url.
     * @param room - Notification room name.
     * @notExported
     */
    function onNewNotification(type: string, message: string, url?: string, room?: string) {
      switch (type) {
        case 'newMessage':
          if (room && url && ((roomData && roomData.name !== room) || !roomData)) {
            setShowNotification(true)
            setNotificationData({
              message: `${t(message)}: ${room}`,
              type: 'success',
              duration: 'short',
              action: <CaleoYellowButton label={t('chat.goto')} valid clickAction={() => navigate(url)} />,
            })
          }
          break

        case 'general':
          setShowNotification(true)
          setNotificationData({
            message,
            type: 'success',
            duration: 'long',
          })
          break

        default:
          break
      }
    }

    /**
     * Event handler for connection.
     * @notExported
     */
    function onConnect() {
      if (user) {
        setIsConnected(true)
        setConnecting(false)
        sendUser(user.id)
        getRoomsInvites()
      }
    }

    /**
     * Event handler for disconnection.
     * @notExported
     */
    function onDisconnect() {
      setIsConnected(false)
      setRoomData(undefined)
      setConnecting(false)
    }

    /**
     * Event handler for error.
     *
     * @param error - Error details.
     * @notExported
     */
    function onError(error: IError) {
      if (error.name === 'CanceledError') return

      // Transient: when joining a room that was just created the socket server
      // may briefly respond with 404 'No room found'. That is expected in
      // the create->join race and will be retried; ignore that to avoid
      // log spam and unnecessary global error state.
      if (error.status === 404 && (error.error === 'No room found' || error.message === 'No room found')) {
        console.debug('WebSocket transient 404 (No room found) - ignoring')
        return
      }

      console.error('WebSocket error', error)
      setError(error)
    }

    socket.on('memberJoin', onMemberJoin)
    socket.on('memberLeave', onMemberLeave)
    socket.on('invite', onInvite)
    socket.on('roomEdit', onRoomEdit)
    socket.on('roomDelete', onRoomDelete)
    socket.on('newMessage', onNewMessage)
    socket.on('messageDeleted', onMessageDeleted)
    socket.on('newNotification', onNewNotification)
    socket.on('connect', onConnect)
    socket.on('disconnect', onDisconnect)
    socket.on('error', onError)

    return () => {
      socket.off('memberJoin', onMemberJoin)
      socket.off('memberLeave', onMemberLeave)
      socket.off('invite', onInvite)
      socket.off('roomEdit', onRoomEdit)
      socket.off('roomDelete', onRoomDelete)
      socket.off('newMessage', onNewMessage)
      socket.off('messageDeleted', onMessageDeleted)
      socket.off('newNotification', onNewNotification)
      socket.off('connect', onConnect)
      socket.off('disconnect', onDisconnect)
      socket.off('error', onError)
    }
  }, [isConnected, roomData, userRooms, userInvites, connecting, user, t, navigate, sendUser, getRoomsInvites])

  const providerValue = useMemo(
    () => ({
      isConnected,
      chatSecret,
      allowedUsers,
      userRooms,
      roomData,
      userInvites,
      newMessage,
      loading,
      error,
      fullyRendered,
      connecting,
      showNotification,
      notificationData,
      closeNotification,
      setFullyRendered,
      setIsConnected,
      setUserRooms,
      setRoomData,
      setUserInvites,
      setLoading,
      setError,
      connect,
      joinRoom,
      leaveRoom,
      sendMessage,
      deleteMessage,
      createRoom,
      saveRoom,
      deleteRoom,
      getRoomsInvites,
      sendUser,
      invite,
      getMoreMessages,
      sendNotification,
      reconnect,
      disconnect,
    }),
    [
      isConnected,
      chatSecret,
      allowedUsers,
      userRooms,
      roomData,
      userInvites,
      newMessage,
      loading,
      error,
      fullyRendered,
      connecting,
      showNotification,
      notificationData,
      closeNotification,
      setFullyRendered,
      setIsConnected,
      setUserRooms,
      setRoomData,
      setUserInvites,
      setLoading,
      setError,
      connect,
      joinRoom,
      leaveRoom,
      sendMessage,
      deleteMessage,
      createRoom,
      saveRoom,
      deleteRoom,
      getRoomsInvites,
      sendUser,
      invite,
      getMoreMessages,
      sendNotification,
      reconnect,
      disconnect,
    ]
  )

  return <Provider value={providerValue}>{children}</Provider>
}

export default ChatProvider
